import React, { useState, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import { FIELD_TYPES } from "../../utils/constants";
import Select from "react-select";
import { getReactSelectTheme } from "../../utils/ReactSelectStyles";
import LookupsAPI from "../../API/lookups.api";
import ValidationError from "../../components/Shared/ValidationError";
import ParameterConstraints from "./ParameterConstraints";

const AlertConfiguration = ({
    data,
    setData,
    parameterConstraints,
    setParameterConstraints,
    isFormSubmitted,
    error,
}) => {
    //General states
    const { t } = useTranslation();
    const [isDataLoaded, setisDataLoaded] = useState(false);

    //Data states
    const [materialsLookup, setMaterialsLookup] = useState([]);
    const [devicesLookup, setDevicesLookup] = useState([]);
    const [lotsLookup, setLotsLookup] = useState([]);
    const [suppliersLookup, setSuppliersLookup] = useState([]);

    //Form field constants
    const alertConfigurationFields = [
        {
            required: true,
            label: t("name"),
            name: "name",
            type: FIELD_TYPES.Text,
        },
        {
            colFields: true,
            fields: [
                {
                    required: true,
                    label: t("material"),
                    name: "material",
                    type: FIELD_TYPES.Dropdown,
                    options: materialsLookup,
                },
                {
                    label: t("devices"),
                    name: "devices",
                    type: FIELD_TYPES.Dropdown,
                    isMulti: true,
                    options: devicesLookup,
                },
                {
                    label: t("lots"),
                    name: "lots",
                    type: FIELD_TYPES.Dropdown,
                    isMulti: true,
                    options: lotsLookup,
                    isDisabled: !data.material,
                },

                {
                    label: t("suppliers"),
                    name: "suppliers",
                    type: FIELD_TYPES.Dropdown,
                    isMulti: true,
                    options: suppliersLookup,
                    isDisabled: !data.material,
                },
            ],
        },
    ];

    //Use effects
    useEffect(() => {
        fetchLookups();
    }, []);

    useEffect(() => {
        if (data?.material?.value) {
            getLotsLookup(data.material.value);
            getSuppliersLookup(data.material.value);
            isDataLoaded && resetMaterialDependentFields();
            setisDataLoaded(true);
        }
    }, [data.material]);

    //Data fetching
    const fetchLookups = () => {
        getMaterialsLookup();
        getDevicesLookup();
    };

    const getMaterialsLookup = async () => {
        const result = await LookupsAPI.getMaterials();
        if (result)
            setMaterialsLookup(
                result.map((elem) => ({ value: elem.id, label: elem.name }))
            );
    };

    const getDevicesLookup = async () => {
        const result = await LookupsAPI.getScanners(false);
        if (result) setDevicesLookup(result);
    };

    const getLotsLookup = async (materialId) => {
        const result = await LookupsAPI.getLots(materialId);
        if (result) setLotsLookup(result);
    };

    const getSuppliersLookup = async (materialId) => {
        const result = await LookupsAPI.getSuppliers(materialId);
        if (result) setSuppliersLookup(result);
    };

    //Handlers
    const handleSelectChange = (name, choice) => {
        setData((prevData) => ({ ...prevData, [name]: choice }));
    };

    const handleInputChange = (e) => {
        setData((prevData) => ({
            ...prevData,
            [e.target.name]: e.target.value,
        }));
    };

    const resetMaterialDependentFields = () => {
        setData((prevData) => ({ ...prevData, lots: [], suppliers: [] }));
        setParameterConstraints([{}]);
    };

    const fieldsStateMachine = (field) => {
        switch (field?.type) {
            case FIELD_TYPES.Text:
                return (
                    <>
                        <input
                            type="text"
                            className="input-element"
                            placeholder={field.label}
                            onChange={handleInputChange}
                            name={field.name}
                            value={data[field.name] || ""}
                            disabled={field.isDisabled}
                        />
                        {isFormSubmitted && (
                            <ValidationError errorMessage={error[field.name]} />
                        )}
                    </>
                );

            case FIELD_TYPES.Dropdown:
                return (
                    <>
                        <Select
                            isMulti={field.isMulti}
                            name={field.name}
                            value={data[field.name]}
                            options={field.options}
                            onChange={(choice) =>
                                handleSelectChange(field.name, choice)
                            }
                            isDisabled={field.isDisabled}
                            placeholder={t("selectPlaceholder", {
                                entity: field.label,
                            })}
                            theme={getReactSelectTheme}
                        />
                        {isFormSubmitted && (
                            <ValidationError errorMessage={error[field.name]} />
                        )}
                    </>
                );
        }
    };

    //Renderers
    const renderAlertConfigurationFields = () => {
        return alertConfigurationFields.map((field, index) =>
            !field.colFields ? (
                <div key={index} className="input-group">
                    <label
                        className={`uppercase ${
                            field.required ? "required" : ""
                        }`}
                    >
                        {field.label}
                    </label>
                    {fieldsStateMachine(field)}
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-x-5" key={index}>
                    {field.fields.map((colField, index) => (
                        <div key={index} className="input-group">
                            <label
                                className={`uppercase ${
                                    colField.required ? "required" : ""
                                }`}
                            >
                                {colField.label}
                            </label>
                            {fieldsStateMachine(colField)}
                        </div>
                    ))}
                </div>
            )
        );
    };

    return (
        <>
            <h3 className="text-primary-color-blue font-medium capitalize">
                {
                    <Trans i18nKey="alertConfigurations">
                        Alert Configurations
                    </Trans>
                }
            </h3>
            <hr className="mt-2 mb-5" />
            {renderAlertConfigurationFields()}
            <ParameterConstraints
                materialId={data?.material?.value}
                parameterConstraints={parameterConstraints}
                setParameterConstraints={setParameterConstraints}
                isFormSubmitted={isFormSubmitted}
                error={error}
            />
        </>
    );
};

export default AlertConfiguration;
